import { PageController } from '@client/components/_core/layout/PageController';
import { Tabs } from '@client/components/_core/navigation/Tabs';
import { AiAssistantButton } from '@client/components/_shared/ai/AiAssistantButton';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { ICONS } from '@client/styles/theme';
import { trpc } from '@client/trpc/client';
import { createFileRoute } from '@tanstack/react-router';
import { Skeleton } from 'antd';
import { Header } from './Header';
import { conflictOfInterestFormStore } from './store';

export const Route = createFileRoute('/console/$orgId/compliance/coi/details/$id')({
    staticData: {
        breadcrumb: {
            title: 'Conflict of interest details',
        },
        ui: {
            widthVariant: 'default',
        },
        entity: 'conflictOfInterest',
    },
    params: {
        parse({ id }) {
            return {
                id: BigInt(id),
            };
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <CoiDetails />
        </PageController>
    );
}

function CoiDetails() {
    const { id } = Route.useParams();
    const { trpcUtils } = useTrpc();
    const updateConflictOfInterest = trpc.conflictOfInterest.updateConflictOfInterest.useMutation();

    // Fetch COI data with tRPC query
    const coiQuery = trpc.conflictOfInterest.getConflictOfInterest.useQuery(
        { id },
        {
            enabled: !!id,
            throwOnError(error) {
                errorMessage.show(error);
                return false;
            },
        },
    );

    const { formData: conflictOfInterest } = conflictOfInterestFormStore.useInit({
        entity: 'conflictOfInterest',
        data: coiQuery.data,
        isLoading: coiQuery.isLoading,

        async onReload() {
            await coiQuery.refetch();
        },

        async onSave(currentValue, newValue) {
            try {
                await updateConflictOfInterest.mutateAsync({
                    id: currentValue.id,
                    set: newValue,
                });

                await trpcUtils.conflictOfInterest.invalidate();

                return { success: true };
            } catch (e) {
                return { success: false, error: e };
            }
        },
    });

    return id === conflictOfInterest?.id ? (
        <>
            <Header />
            <Tabs
                items={[
                    {
                        label: (
                            <span className="flex items-center gap-2">
                                <ICONS.manage size={16} />
                                Manage
                            </span>
                        ),
                        to: '/console/$orgId/compliance/coi/details/$id/manage',
                    },
                    {
                        label: (
                            <span className="flex items-center gap-2">
                                <ICONS.library size={16} />
                                Library
                            </span>
                        ),
                        to: '/console/$orgId/compliance/coi/details/$id/library',
                    },
                ]}
                rightAddon={
                    <AiAssistantButton
                        entityType="conflictOfInterest"
                        entityId={conflictOfInterest.id}
                        entityName="Conflict of Interest"
                        outputFormat="markdown"
                    />
                }
            />
        </>
    ) : (
        <Skeleton active />
    );
}
